import React, { useContext, useState } from 'react';
import classNames from 'classnames';
import _isEmpty from 'lodash/isEmpty';
import PropTypes from 'prop-types';
import { defineMessages } from 'react-intl';
import { useSelector } from 'react-redux';

import ComponentErrorBoundary from 'components/ComponentErrorBoundary';
import Message from 'components/Message';
import { ColumnLayout, Column, Title, Controls, Control } from 'features/App';
import ScheduleDateControl from 'features/Schedules/ScheduleCalendar/ScheduleDateControl';
import { useIsScheduleCalendarV2Enabled } from 'hooks/useIsScheduleCalendarV2Enabled';
import { useIsScheduleDetailsV2Enabled } from 'hooks/useIsScheduleDetailsV2Enabled';
import { useIsScheduleFiltersV2Enabled } from 'hooks/useIsScheduleFiltersV2Enabled';
import { useIsScheduleListV2Enabled } from 'hooks/useIsScheduleListV2Enabled';
import { selectActiveVenue } from 'selectors/general';

import VenueSchedulesContext from './Contexts/VenueSchedulesContext';
import EmptyDetail from './EmptyDetail';
import ScheduleDetail from './ScheduleDetailContainer';
import ScheduleAddClassButton from './ScheduleAddClassButton';
import ScheduleControlsHeightSync from './ScheduleControlsHeightSync';
import ScheduleDetailV2 from './ScheduleDetailV2';
import ScheduleFilterChipsContainer from './ScheduleFilterChipsContainer';
import { ScheduleList } from './ScheduleList';
import ScheduleListFilterContainer from './ScheduleListFilterContainer';
import ScheduleListV2EmptyState from './ScheduleListV2/ScheduleListV2EmptyState';
import { ScheduleListV2 } from './ScheduleListV2';
import { useIsScheduleListV2EmptyDay } from './ScheduleListV2/useIsScheduleListV2EmptyDay';
import ScheduleTodayButton from './ScheduleTodayButton';
import { VenueSmartSpotStatus } from './SmartSpot';

const messages = defineMessages({
  title: {
    id: 'yield-management.header.title',
    defaultMessage: 'Classes',
  },
});

const YieldManagement = ({ venueId, startDate, startDateFormatted, updateStartDate }) => {
  const { activeSchedule: selectedSchedule } = useContext(VenueSchedulesContext);
  const [scrollPos, setScrollPos] = useState(0);
  const isScheduleCalendarV2Enabled = useIsScheduleCalendarV2Enabled(venueId);
  const isScheduleDetailsV2Enabled = useIsScheduleDetailsV2Enabled(venueId);
  const isScheduleFiltersV2Enabled = useIsScheduleFiltersV2Enabled(venueId);
  const isScheduleListV2Enabled = useIsScheduleListV2Enabled();
  const showScheduleListV2EmptyDay = useIsScheduleListV2EmptyDay();
  const { isManualStudio, timezone: venueTimezone } = useSelector(selectActiveVenue);
  const hasPageScroll = isScheduleDetailsV2Enabled === true && !showScheduleListV2EmptyDay;
  const hasStickyControls = hasPageScroll && isScheduleFiltersV2Enabled === true;

  const listOnFocusEnter = () => {
    if (scrollPos >= 0) {
      window.scrollTo(0, scrollPos);
    }
  };
  const listOnFocusLeave = () => {
    setScrollPos(window.scrollY);
  };

  const ScheduleListComponent = isScheduleListV2Enabled === true ? ScheduleListV2 : ScheduleList;

  const todayButton = isScheduleCalendarV2Enabled === true ? <ScheduleTodayButton venueId={venueId} /> : null;
  const DetailComponent = isScheduleDetailsV2Enabled === true ? ScheduleDetailV2 : ScheduleDetail;
  const controlsClassName = isScheduleFiltersV2Enabled === true ? 'yield-management__controls--filters-v2' : undefined;

  return (
    <ColumnLayout className={hasPageScroll ? 'yield-management--page-scroll' : undefined}>
      <Column
        className={classNames(
          showScheduleListV2EmptyDay
            ? 'column-layout__column--lg-1/1 yield-management__empty-day-column'
            : 'column-layout__column--lg-5/12 yield-management__list-column'
        )}
        name="list"
        type="flush"
        onFocusEnter={listOnFocusEnter}
        onFocusLeave={listOnFocusLeave}>
        {hasStickyControls && <ScheduleControlsHeightSync />}
        <Message message={messages.title} as={Title} />
        <Controls className={controlsClassName}>
          <Control type="filter" className={todayButton ? 'schedule-date-controls' : undefined}>
            <ScheduleDateControl setDate={updateStartDate} venueId={venueId} />
            {todayButton}
          </Control>
          {isScheduleFiltersV2Enabled === true && (
            <Control type="button">
              <ScheduleAddClassButton startDate={startDate} venueId={venueId} />
            </Control>
          )}
          {isScheduleFiltersV2Enabled !== true && (
            <Control type="filter">
              <ScheduleListFilterContainer />
            </Control>
          )}
          <Control type="filter" className="schedule-smartspot-control">
            <VenueSmartSpotStatus className="yield-management__smartspot-toggle" />
          </Control>
          {isScheduleFiltersV2Enabled === true && (
            <Control type="trough">
              <ScheduleFilterChipsContainer />
            </Control>
          )}
        </Controls>
        <ComponentErrorBoundary>
          {showScheduleListV2EmptyDay ? (
            <ScheduleListV2EmptyState
              isManualStudio={isManualStudio}
              startDate={startDate}
              venueId={venueId}
              venueTimezone={venueTimezone}
            />
          ) : (
            <ScheduleListComponent
              key={`${venueId}-${startDateFormatted}`}
              className="yield-management__schedule-list"
              venueId={venueId}
            />
          )}
        </ComponentErrorBoundary>
      </Column>
      {!showScheduleListV2EmptyDay && (
        <Column className="column-layout__column--lg-7/12 lg-pl-l yield-management__details-column" name="detail">
          <Controls />
          <ComponentErrorBoundary>
            {_isEmpty(selectedSchedule) ? <EmptyDetail /> : <DetailComponent key={selectedSchedule.scheduleId} />}
          </ComponentErrorBoundary>
        </Column>
      )}
    </ColumnLayout>
  );
};

YieldManagement.propTypes = {
  venueId: PropTypes.number,
  startDate: PropTypes.instanceOf(Date),
  startDateFormatted: PropTypes.string,
  updateStartDate: PropTypes.func,
};

export default YieldManagement;
